import { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { message } from 'antd';
import useSwr from '@hooks/useSwr';
import SessionContext from '@providers/Session/SessionContext';
import api from '@services/api';
import { useNotificationSound } from '@hooks/useNotificationSound';

interface NotificationItem {
  disparo: string;
  idAlarme: string;
  nomeAlarme: string;
  quantidadeDisparo: string;
  tipoAlarme: string;
  tipoAlarmeOriginal: string | null;
  tipoDisparo: string;
  valorDisparo: string;
  nomeGrafico: string;
  timeId: string;
}

export const useNotifications = () => {
  const { t } = useTranslation(['main', 'alarms-notifications']);
  const navigate = useNavigate();
  const { customersSwitch } = useContext(SessionContext);

  const [removedNotifications, setRemovedNotifications] = useState<Set<string>>(
    new Set(),
  );
  const [loadingNotifications, setLoadingNotifications] = useState<Set<string>>(
    new Set(),
  );

  const {
    data: notifications,
    isLoading: isNotificationsLoading,
    mutate: mutateNotifications,
  } = useSwr(
    '/service-proxy/dashboard/simcard_online/alarme/kapacitor/alerta_task/listar/notificacao',
    {
      headers: {
        customerId: customersSwitch?.selected,
      },
    },
    {
      refreshInterval: 300000,
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      dedupingInterval: 60000,
    },
  );

  const filteredNotifications = useMemo(() => {
    if (!notifications) return [];
    return notifications
      .filter(
        (notification: NotificationItem) =>
          !removedNotifications.has(notification.idAlarme),
      )
      .sort((a: NotificationItem, b: NotificationItem) => {
        // Ordenar por timeId (mais recente primeiro)
        return new Date(b.timeId).getTime() - new Date(a.timeId).getTime();
      });
  }, [notifications, removedNotifications]);

  const { previousNotificationCount } = useNotificationSound(
    filteredNotifications,
  );

  const handleRemoveNotification = useCallback(
    async (idAlarme: string, timeId: string) => {
      setLoadingNotifications(prev => new Set(prev).add(idAlarme));

      try {
        await api.patch(
          `/service-proxy/dashboard/simcard_online/alarme/kapacitor/alerta_task/atualiza_visto`,
          {
            idTask: idAlarme,
            timeId,
          },
          {
            headers: {
              customerId: customersSwitch?.selected,
            },
          },
        );

        setRemovedNotifications(prev => new Set(prev).add(idAlarme));
        mutateNotifications();
        message.success(t('alarms-notifications:removeNotificationSuccess'));
      } catch (error) {
        message.error(t('alarms-notifications:removeNotificationError'));
      } finally {
        setLoadingNotifications(prev => {
          const newSet = new Set(prev);
          newSet.delete(idAlarme);
          return newSet;
        });
      }
    },
    [customersSwitch?.selected, mutateNotifications, t],
  );

  const handleNotificationClick = useCallback(
    (notification: NotificationItem, onClose?: () => void) => {
      if (onClose) {
        onClose();
      }

      const params: Record<string, string> = {
        nomeAlarme: notification.nomeAlarme,
        level: notification.disparo,
        nomeGrafico: notification.nomeGrafico,
        page: '1',
      };

      // Só adiciona tipoAlarme se não estiver usando tipoAlarmeOriginal
      if (!notification.tipoAlarmeOriginal) {
        params.tipoAlarme = notification.tipoAlarme;
      }

      const searchParams = new URLSearchParams(params);

      navigate(
        `/indicators/alarms/page-alarms-notification?${searchParams.toString()}`,
      );
    },
    [navigate],
  );

  const handleNavigateToAlarmConfigPage = useCallback(
    () => navigate('/indicators/alarms/page-alarms'),
    [navigate],
  );

  const handleNavigateToAlarmPage = useCallback(
    () => navigate('/indicators/alarms/page-alarms-notification'),
    [navigate],
  );

  const formatAlarmType = useCallback(
    (alarm: string, alarmOriginal?: string | null) => {
      const displayAlarm = alarmOriginal || alarm;

      if (displayAlarm === 'threshold') {
        return t('alarms-notifications:threshold');
      }
      if (displayAlarm === 'variacao_percentual') {
        return t('alarms-notifications:percentualVariation');
      }
      if (displayAlarm === 'sazonal') {
        return t('alarms-notifications:seasonal');
      }

      return '';
    },
    [t],
  );

  return {
    notifications,
    filteredNotifications,
    isNotificationsLoading,
    removedNotifications,
    loadingNotifications,
    handleRemoveNotification,
    handleNotificationClick,
    handleNavigateToAlarmConfigPage,
    handleNavigateToAlarmPage,
    formatAlarmType,
    mutateNotifications,
    previousNotificationCount,
  };
};

export type { NotificationItem };
